import {
    Avatar,
    Card,
    Colors,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    defaultHover,
    type IconName,
} from '@amelio/ui-library';

import { AvatarStack } from '../AvatarStack/AvatarStack';
import { InfoBadge } from '../InfoBadge/InfoBadge';
import { UserCard } from '../UserCard/UserCard';

import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

export interface ProfileInfoBadge {
    /** Unique identifier for the badge */
    id: string;
    /** Name of a library registry icon (e.g., "business-chart", "essential-checkmark") */
    icon: IconName;
    /** Main label text (e.g., "Marketing", "360 Evaluation") */
    primaryLabel: string;
    /** Optional secondary label text (e.g., "Employee Profile", "Completed on April 22nd, 2025") */
    secondaryLabel?: string;
    /** Optional callback when badge is clicked */
    onClick?: () => void;
}

export interface ProfileCardProps {
    /** User object with profile information */
    user?: User;
    /** Array of info badges for metadata display */
    infoBadges?: ProfileInfoBadge[];
    /** Array of reviewers or collaborators */
    reviewers?: User[];
    /** Additional CSS classes for custom styling */
    className?: string;
    /** Data-testid attribute for testing */
    dataTestId?: string;
    /** Review instance object */
    reviewInstance: ReviewInstance;
    /** Callback when card is clicked to navigate to results */
    onViewResults: (reviewInstance: ReviewInstance) => void;
}

/**
 * Determines if reviewers section should be displayed.
 *
 * @param reviewers - Array of reviewer users
 * @returns True if reviewers exist and array is not empty
 */
const shouldDisplayReviewers = (reviewers: User[] | undefined): boolean => {
    return Boolean(reviewers && reviewers.length > 0);
};

/**
 * ProfileCard component displays comprehensive user information in a card format.
 *
 * Features:
 * - User identity (avatar, name, title) via UserCard component
 * - Multiple info badges for metadata (department, profile type, evaluation status)
 * - Reviewers/collaborators display via AvatarStack component
 */
export const ProfileCard = ({
    user,
    infoBadges,
    reviewers,
    className = '',
    dataTestId = 'profile-card',
    reviewInstance,
    onViewResults,
}: ProfileCardProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle } = useCompanyInformation();

    if (!user) {
        return null;
    }

    const fullName = `${user.firstName} ${user.lastName}`;
    const userTitle = getJobTitle(user.jobTitleId || '');
    const hasReviewers = shouldDisplayReviewers(reviewers);
    const reviewersCount = reviewers?.length || 0;

    return (
        <Card
            variant={'flat'}
            padding={'none'}
            bgColor={'bg-white'}
            interactive
            hoverVariant={'subtle'}
            onClick={() => onViewResults(reviewInstance)}
            className={`max-h-100 gap-3 border border-white/80 bg-white/70 px-4 py-4 backdrop-blur sm:px-8 sm:py-6 ${className}`}
            dataTestId={dataTestId}
        >
            {/* User Section: Avatar + Name + Title. `min-w-0` lets the column shrink inside
                the card so the name/title can clip instead of overflowing; the tooltip
                reveals the full text on hover. */}
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className={'min-w-0'}>
                            <UserCard
                                avatar={
                                    <Avatar
                                        src={getProfilePictureUrl(user?.profilePictureUrl || '')}
                                        initials={getInitials(user)}
                                        alt={fullName}
                                        size={'xs'}
                                        variant={'circular'}
                                    />
                                }
                                name={fullName}
                                description={userTitle}
                                size={'large'}
                                alt={`${fullName} profile`}
                                textColor={Colors.BASE}
                                descriptionTextColor={Colors.BASE_400}
                                nameClassName={'truncate overflow-hidden whitespace-nowrap'}
                                descriptionClassName={
                                    'text-caption truncate overflow-hidden whitespace-nowrap block'
                                }
                                className={'p-0'}
                            />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent
                        side={'top'}
                        className={'pointer-events-auto max-w-md'}
                    >
                        <p>{fullName}</p>
                        {userTitle && <p>{userTitle}</p>}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            {/* Info Badges Section: Department, Profile Type, Evaluation Status */}
            {infoBadges && infoBadges.length > 0 && (
                <div
                    className={'mt-5 flex flex-col gap-6'}
                    data-testid={`${dataTestId}-info-badges`}
                    role={'list'}
                    aria-label={t('common.profileInfoBadgesAriaLabel')}
                >
                    {infoBadges.map((badge) => (
                        <InfoBadge
                            key={badge.id}
                            icon={badge.icon}
                            primaryLabel={badge.primaryLabel}
                            secondaryLabel={badge.secondaryLabel}
                            size={'sm'}
                            ariaLabel={`${badge.primaryLabel}${badge.secondaryLabel ? `: ${badge.secondaryLabel}` : ''}`}
                            onClick={badge.onClick}
                            className={badge.onClick ? defaultHover : ''}
                        />
                    ))}
                </div>
            )}

            {/* Reviewers Section: AvatarStack + Count Label */}
            {hasReviewers ? (
                <div
                    className={'mt-10 flex flex-col gap-4'}
                    data-testid={`${dataTestId}-reviewers`}
                    role={'region'}
                    aria-label={t('common.reviewersSectionAriaLabel')}
                >
                    <AvatarStack
                        users={reviewers as User[]}
                        maxVisible={4}
                        size={'2xs'}
                        spacing={'tight'}
                        showBorder
                        borderColor={Colors.BASE_50}
                        showTooltip
                        dataTestId={`${dataTestId}-reviewers-stack`}
                    />

                    <Text
                        size={'small'}
                        weight={'regular'}
                        color={Colors.BASE_400}
                        className={'text-xs'}
                    >
                        {t(
                            reviewersCount === 1
                                ? 'common.reviewerWithCount'
                                : 'common.reviewersWithCount',
                            { count: reviewersCount },
                        )}
                    </Text>
                </div>
            ) : null}
        </Card>
    );
};
